import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ScreenHeader } from '../../components/ScreenHeader';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ReportDownloadProgressView } from '../../components/ReportDownloadProgress';
import { ReportPreviewTable } from '../../components/ReportPreviewTable';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { getReportModuleCapabilities } from '../../config/reportCapabilities';
import { getLocalReportMeta } from '../../config/reportCatalogue';
import { PERMISSIONS } from '../../constants/permissions';
import { routes, type RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAuth } from '../../context/AuthContext';
import { useAppColors } from '../../context/PreferencesContext';
import { useReportDownloads } from '../../context/ReportDownloadContext';
import { runReportPreview } from '../../services/reportService';
import type { ReportPreview } from '../../types/report';
import { normalizeReportFilters } from '../../utils/reportFilters';
import { reportErrorMessage } from '../../utils/reportErrors';

type Props = NativeStackScreenProps<RootStackParamList, 'ReportPreview'>;

export function ReportPreviewScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      meta: {
        ...typography.caption,
        color: palette.textSecondary,
        marginBottom: spacing.sm,
      },
      help: {
        ...typography.caption,
        color: palette.textMuted,
        marginBottom: spacing.sm,
      },
      warning: {
        ...typography.caption,
        color: palette.warning,
        marginBottom: spacing.md,
      },
      error: { ...typography.body, color: palette.error, marginBottom: spacing.sm },
    }),
    [palette],
  );

  const auth = useAuth();
  const capabilities = getReportModuleCapabilities(auth.permissions);
  const downloads = useReportDownloads();
  const { reportCode, filters } = route.params;
  const meta = getLocalReportMeta(reportCode);

  const [preview, setPreview] = useState<ReportPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!capabilities.canPreview || !meta) {
      setError('You do not have permission to preview this report.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await runReportPreview({ reportCode, filters });
      setPreview(result);
      setError(null);
    } catch (err) {
      setPreview(null);
      setError(reportErrorMessage(err, 'Unable to preview this report.'));
    } finally {
      setLoading(false);
    }
  }, [capabilities.canPreview, filters, meta, reportCode]);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  const activeProgress = useMemo(
    () =>
      downloads.activeDownloads.find(
        item =>
          item.downloadId.includes(`${reportCode}:`) &&
          (item.phase === 'preparing' ||
            item.phase === 'downloading' ||
            item.phase === 'saving'),
      ) ?? null,
    [downloads.activeDownloads, reportCode],
  );

  const exportFormat = async (format: 'CSV' | 'XLSX') => {
    if (!meta) return;
    setDownloadError(null);
    if (downloads.isDownloadInFlight(reportCode, format)) {
      setDownloadError('A download is already in progress.');
      return;
    }
    try {
      const normalized = normalizeReportFilters(reportCode, filters);
      const result = await downloads.startDownload({
        reportCode,
        reportName: meta.reportName,
        format,
        filters: normalized,
        view: preview
          ? {
              columns: preview.columns,
              rows: preview.rows.map(row =>
                Object.fromEntries(
                  preview.columns.map(column => {
                    const value = row.values[column.key];
                    return [
                      column.key,
                      typeof value === 'string' ||
                      typeof value === 'number' ||
                      typeof value === 'boolean' ||
                      value === null
                        ? value
                        : value == null
                          ? null
                          : String(value),
                    ];
                  }),
                ),
              ),
            }
          : undefined,
      });
      navigation.navigate(routes.reportDownloadResult, {
        downloadId: result.downloadId,
      });
    } catch (err) {
      setDownloadError(
        reportErrorMessage(err, 'The report could not be saved.'),
      );
    }
  };

  if (!meta) {
    return (
      <PermissionGuard
        permission={PERMISSIONS.REPORT_VIEW}
        fallback={<UnauthorizedScreen />}
      >
        <AppScreen webLayout="wide">
          <ErrorState
            message="Report was not found."
            onRetry={() => navigation.goBack()}
          />
        </AppScreen>
      </PermissionGuard>
    );
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.REPORT_VIEW}
      fallback={<UnauthorizedScreen />}
    >
      {loading && !preview ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title={meta?.reportName ?? 'Report preview'}
            onBack={navigation.goBack}
          />
          <AppLoader message="Loading preview" />
        </AppScreen>
      ) : error && !preview ? (
        <AppScreen contentStyle={styles.center}>
          <ScreenHeader
            title={meta?.reportName ?? 'Report preview'}
            onBack={navigation.goBack}
          />
          <ErrorState message={error} onRetry={() => load()} />
          <View style={styles.actions}>
            {capabilities.canExportCsv || capabilities.canExportXlsx ? (
              <Text style={themed.help}>
                Preview failed, but export may still work.
              </Text>
            ) : null}
            {capabilities.canExportCsv ? (
              <AppButton
                title="Download CSV"
                variant="secondary"
                onPress={() => exportFormat('CSV')}
              />
            ) : null}
            {capabilities.canExportXlsx ? (
              <AppButton
                title="Download XLSX"
                variant="secondary"
                onPress={() => exportFormat('XLSX')}
              />
            ) : null}
          </View>
        </AppScreen>
      ) : (
        <AppScreen scroll webLayout="wide">
          <ScreenHeader
            title={meta.reportName}
            onBack={navigation.goBack}
          />
          <View style={styles.body}>
          {preview ? (
            <Text style={themed.meta}>
              Rows: {preview.totalRows}
              {preview.truncated ? ' (preview truncated)' : ''}
            </Text>
          ) : null}
          {preview?.truncated ? (
            <Text accessibilityRole="alert" style={themed.warning}>
              Showing a limited preview. Download an export for the full
              result.
            </Text>
          ) : null}
          {error ? (
            <ErrorState message={error} onRetry={() => load()} />
          ) : null}
          {downloadError ? (
            <Text accessibilityRole="alert" style={themed.error}>
              {downloadError}
            </Text>
          ) : null}

          <ReportDownloadProgressView
            progress={activeProgress}
            canCancel={Boolean(activeProgress)}
            onCancel={() =>
              activeProgress
                ? downloads.cancelDownload(activeProgress.downloadId)
                : undefined
            }
          />

          {preview ? (
            <ReportPreviewTable
              columns={preview.columns}
              rows={preview.rows}
            />
          ) : null}

          <View style={styles.actions}>
            {capabilities.canExportCsv ? (
              <AppButton
                title="Download CSV"
                variant="secondary"
                loading={downloads.isDownloadInFlight(reportCode, 'CSV')}
                disabled={downloads.isDownloadInFlight(reportCode, 'CSV')}
                onPress={() => exportFormat('CSV')}
              />
            ) : null}
            {capabilities.canExportXlsx ? (
              <AppButton
                title="Download XLSX"
                variant="secondary"
                loading={downloads.isDownloadInFlight(reportCode, 'XLSX')}
                disabled={downloads.isDownloadInFlight(reportCode, 'XLSX')}
                onPress={() => exportFormat('XLSX')}
              />
            ) : null}
          </View>
          </View>
        </AppScreen>
      )}
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg },
  center: { justifyContent: 'center' },
  actions: { gap: spacing.sm, marginTop: spacing.lg, marginBottom: spacing.xl },
});
